import { ApplicationConfig, ErrorHandler, inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { UrlSerializer, provideRouter, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withInterceptors, withJsonpSupport, withXhr } from '@angular/common/http';
import { GCMRoutes } from './app.routes';
import { APOLLO_OPTIONS, Apollo } from 'apollo-angular'
import { HttpLink } from 'apollo-angular/http';
import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client/core';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { SetContextLink } from '@apollo/client/link/context';
import { HttpHeaders } from '@angular/common/http';
import { provideNgIdleKeepalive } from '@ng-idle/keepalive'
import { EnvironmentVariablesService } from './services/environment-variables.service';
import { ErrorLink } from '@apollo/client/link/error';
import { LoggingService } from './services/logging.service';
import { GlobalErrorHandler } from './services/global-error-handler';
import { BASE_ENV_CONFIG } from '@gcm/gcm-ui-angular';
import { environment } from 'src/environments/environment';
import { AuthClientConfig, provideAuth0 } from '@auth0/auth0-angular';
import { GcmUrlSerializer } from './GcmUrlSerializer';
import { authHttpInterceptorFn } from '@auth0/auth0-angular';
import { provideHighcharts } from 'highcharts-angular';
import { provideUiTour } from 'ngx-ui-tour-md-menu';
import Highcharts from 'highcharts';
import 'highcharts/modules/map';
import 'highcharts/modules/funnel';

export function setupAuth0Config(evService: EnvironmentVariablesService, config: AuthClientConfig) {
  const apiUri = location.origin.includes('localhost:4200') ? 'https://localhost:5001/api/*' : location.origin + '/api/*';
  const proxyUri = evService.proxy.hostname + "/*";
  const auth0Audience = evService.auth0Config.audience;
  config.set({
    domain: evService.auth0Config.Domain,
    clientId: evService.auth0Config.ClientId,
    errorPath: '/auth-error',
    authorizationParams: {
      redirect_uri: window.location.origin + '/investor',
      scope: evService.auth0Config.Scope,
      audience: auth0Audience,
    },
    httpInterceptor: {
      allowedList: [
        {
          uri: apiUri,
          tokenOptions: {
            authorizationParams: {
              audience: auth0Audience,
            },
          },
        },
        {
          uri: 'api/*',
          tokenOptions: {
            authorizationParams: {
              audience: auth0Audience,
            },
          },
        },
        {
          uri: proxyUri,
          tokenOptions: {
            authorizationParams: {
              audience: auth0Audience,
            },
          }
        }
      ],
    }
  })
}

export function createApollo(httpLink: HttpLink, evService: EnvironmentVariablesService, loggingService: LoggingService): ApolloClient.Options {
  const errorLink = new ErrorLink(({ error }) => {
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach(({ message, locations, path }) =>
        loggingService.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
      );
    } else if (error) {
      const networkError = JSON.parse(JSON.stringify(error));
      networkError?.error?.errors?.forEach((element: { message: string }) => {
        loggingService.log(element.message);
      });
      loggingService.log(error.message);
    }
  });

  const auth = new SetContextLink(() => {
    return {
      headers: new HttpHeaders().set('Authorization', `Bearer ${evService.contentful['token']}`),
    };
  });

  const uri = `https://graphql.contentful.com/content/v1/spaces/${evService.contentful['spaceId']}/environments/pre-auth`; // <-- add the URL of the GraphQL server here
  return {
    link: ApolloLink.from([errorLink, auth, httpLink.create({ uri })]),
    cache: new InMemoryCache(),
  };
}

export const appConfig: ApplicationConfig = {
  providers:[
    provideAppInitializer(() => {
      const envVariables = inject(EnvironmentVariablesService);
      const auth0Config = inject(AuthClientConfig);
      setupAuth0Config(envVariables, auth0Config);
    }),
    provideHttpClient(withXhr(), withInterceptors([authHttpInterceptorFn]), withJsonpSupport()),
    provideAuth0(),
    {
      provide: UrlSerializer,
      useClass: GcmUrlSerializer
    },
    provideRouter(GCMRoutes, withComponentInputBinding()),
    {
      provide: BASE_ENV_CONFIG,
      useValue: environment,
    },
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpLink, EnvironmentVariablesService, LoggingService],
    },
    Apollo,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideNgIdleKeepalive(),
    provideUiTour(),
    provideHighcharts({ instance: async () => Highcharts }),
    provideZonelessChangeDetection()
  ]
};